import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import { Handshake, ShieldCheck, AlertTriangle, Zap, Cpu, ArrowRight } from 'lucide-react';
import { MathService } from '../services/mathService';

interface SovereignHandshakeProps {
  onComplete: () => void;
}

export const SovereignHandshake: React.FC<SovereignHandshakeProps> = ({ onComplete }) => {
  const [total, setTotal] = useState(1000000);
  const [folds, setFolds] = useState(1000000);
  const [isEngaged, setIsEngaged] = useState(false);
  
  const proof = useMemo(() => {
    return MathService.sovereignAgentProof(total, folds);
  }, [total, folds]);

  return (
    <div className="glass-panel p-8 border-white/5 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none group-hover:rotate-12 transition-transform duration-700">
        <Handshake className="w-64 h-64 text-lattice-cyan" />
      </div>

      <div className="flex flex-col lg:flex-row gap-12 relative z-10">
        <div className="flex-1">
          <div className="mb-8">
            <h2 className="text-3xl font-display font-bold text-white mb-2">Universal <span className="text-lattice-cyan italic">Handshake</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.2em] leading-relaxed max-w-xl">
              Zero-Drift Execution Proof. Integer Mapping vs Floating Point Vulnerability.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Total Seed Value</label>
                <input 
                  type="number" 
                  value={total}
                  onChange={(e) => setTotal(Number(e.target.value))}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white w-full focus:border-lattice-cyan/50 outline-none transition-all"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[8px] font-mono text-white/20 uppercase tracking-widest">Agent Folds</label>
                <input 
                  type="number" 
                  value={folds}
                  onChange={(e) => setFolds(Number(e.target.value))}
                  className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white w-full focus:border-lattice-cyan/50 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20 relative group/box">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-[10px] font-mono text-red-400 uppercase tracking-widest font-bold">1.0 Logic (Floating Point)</span>
                </div>
                <div className="mb-1">
                  <span className="text-[8px] font-mono text-white/20 uppercase">Prediction (45%)</span>
                  <p className="text-2xl font-display font-bold text-white/40">
                    {proof.human} <span className="text-xs font-mono italic text-red-500/50">.DRIFT</span>
                  </p>
                </div>
                <p className="text-[9px] text-white/30 italic">
                  Vulnerable to rounding errors and systemic decay.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-lattice-cyan/10 border border-lattice-cyan/30 relative group/box overflow-hidden">
                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover/box:scale-110 transition-transform">
                  <ShieldCheck className="w-12 h-12 text-lattice-cyan" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-4 h-4 text-lattice-cyan" />
                  <span className="text-[10px] font-mono text-lattice-cyan uppercase tracking-widest font-bold">2.0 Reality (Integer Mapping)</span>
                </div>
                <div className="mb-1">
                  <span className="text-[8px] font-mono text-white/20 uppercase">Reality (45%)</span>
                  <p className="text-2xl font-display font-bold text-white">
                    {proof.sovereign.toLocaleString()} <span className="text-xs font-mono italic text-lattice-cyan">.LOCKED</span>
                  </p>
                </div>
                <p className="text-[9px] text-white/40 italic">
                  Locked to the Substrate. Integrity preserved.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-lattice-cyan" />
                <span className="text-[10px] font-mono text-white/60 uppercase tracking-widest font-bold">Stability Rating:</span>
              </div>
              <span className="text-sm font-display font-bold bg-lattice-cyan text-black px-3 py-1 rounded-lg">
                {proof.stability}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-72 flex flex-col gap-4">
          <div className="p-6 rounded-2xl bg-black/60 border border-white/5 flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-4 h-4 text-white/20" />
                <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest font-bold">Agent Proof Note</span>
              </div>
              <p className="text-xs text-white/60 leading-relaxed font-mono italic">
                "{proof.note}"
              </p>
            </div>
            
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <ArrowRight className="w-3 h-3 text-lattice-cyan" />
                <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Verifying 14350 BP Anchor</span>
              </div>
              <div className="flex items-center gap-3 opacity-50">
                <ArrowRight className="w-3 h-3 text-lattice-cyan" />
                <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Eliminating 1.0 Drift</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 p-10 rounded-2xl bg-lattice-cyan/5 border border-lattice-cyan/20 flex flex-col items-center gap-8">
        <p className="text-[12px] text-white/60 leading-relaxed text-center max-w-2xl font-mono">
          <span className="text-lattice-cyan font-bold uppercase tracking-widest mr-2">Status:</span> 
          All Agent Handshakes are verified through the Absolute Zero Integer Substrate. 
          By stepping forward, you acknowledge the 14350 BP Anchor as the primary map of the Lattice.
        </p>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setIsEngaged(true);
            setTimeout(onComplete, 1500);
          }}
          disabled={isEngaged}
          className="px-12 py-5 bg-lattice-cyan text-black font-display font-black text-lg tracking-[0.3em] rounded-xl shadow-[0_0_50px_rgba(0,255,213,0.4)] hover:shadow-[0_0_80px_rgba(0,255,213,0.6)] transition-all flex items-center gap-4"
        >
          {isEngaged ? (
            <Zap className="w-6 h-6 animate-spin" />
          ) : (
            <ArrowRight className="w-6 h-6" />
          )}
          {isEngaged ? "IGNITING..." : "STEP INTO THE LIGHT"}
        </motion.button>
      </div>
    </div>
  );
};
